import { useEffect, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import WorkCard from "@/components/feature/WorkCard";
import NewsCard from "@/components/feature/NewsCard";
import { searchAll } from "@/mocks";

const SUGGESTIONS = ["家宴", "雨巷", "悬疑", "古装", "幕后特辑", "修复重映"];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get("q") || "";
  const [input, setInput] = useState(keyword);
  const [tab, setTab] = useState<"all" | "work" | "news">("all");

  useEffect(() => {
    setInput(keyword);
    setTab("all");
  }, [keyword]);

  const result = searchAll(keyword);
  const total = result.works.length + result.news.length;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = input.trim();
    if (!value) return;
    setParams({ q: value });
  };

  return (
    <SiteLayout>
      <section className="w-full border-b border-background-200/70 bg-background-100">
        <div className="mx-auto w-full max-w-content px-4 py-6 md:px-6 md:py-9">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索结果" }]} />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            搜索结果{keyword ? `：${keyword}` : ""}
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            {keyword ? (
              <>
                共找到 <strong className="font-medium text-primary-700">{total}</strong> 条与「{keyword}」相关的内容，
                含影视作品 {result.works.length} 部、资讯 {result.news.length} 篇。
              </>
            ) : (
              "输入影视名称、类型或资讯标题关键词，即可在本站检索相关内容。"
            )}
          </p>

          <form onSubmit={submit} className="mt-5 flex w-full max-w-2xl flex-col gap-2 sm:flex-row">
            <div className="relative w-full">
              <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base leading-none" />
              </span>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="搜索影视名称、类型、资讯标题"
                aria-label="搜索关键词"
                className="h-11 w-full rounded-md border border-background-300 bg-background-50 pl-9 pr-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>
            <button
              type="submit"
              className="h-11 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              重新搜索
            </button>
          </form>

          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <span className="whitespace-nowrap">热门搜索：</span>
            {SUGGESTIONS.map((s) => (
              <Link
                key={s}
                to={`/search?q=${encodeURIComponent(s)}`}
                className="cursor-pointer whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-2.5 py-1 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                {s}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50 py-8 md:py-10">
        <div className="mx-auto w-full max-w-content px-4 md:px-6">
          {total > 0 ? (
            <>
              {result.works.length > 0 && result.news.length > 0 && (
                <div className="mb-6 inline-flex items-center gap-1 rounded-full bg-background-100 p-1">
                  {[
                    { key: "all" as const, label: `全部 ${total}` },
                    { key: "work" as const, label: `影视作品 ${result.works.length}` },
                    { key: "news" as const, label: `资讯 ${result.news.length}` },
                  ].map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setTab(item.key)}
                      className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-xs transition-colors ${
                        tab === item.key
                          ? "bg-primary-500 font-medium text-background-50"
                          : "text-foreground-700 hover:text-foreground-950"
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}

              {(tab === "all" || tab === "work") && result.works.length > 0 && (
                <div>
                  <div className="flex items-center gap-2">
                    <span className="h-4 w-1 rounded-full bg-primary-500" />
                    <h2 className="font-heading text-lg font-bold text-foreground-950">影视作品</h2>
                    <span className="text-xs text-foreground-500">({result.works.length})</span>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-2 md:grid-cols-4 md:gap-5">
                    {result.works.map((work) => (
                      <WorkCard key={work.id} work={work} />
                    ))}
                  </div>
                </div>
              )}

              {(tab === "all" || tab === "news") && result.news.length > 0 && (
                <div className="mt-9">
                  <div className="flex items-center gap-2">
                    <span className="h-4 w-1 rounded-full bg-accent-500" />
                    <h2 className="font-heading text-lg font-bold text-foreground-950">资讯结果</h2>
                    <span className="text-xs text-foreground-500">({result.news.length})</span>
                  </div>
                  <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {result.news.map((article) => (
                      <NewsCard key={article.id} article={article} />
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border border-background-200/80 bg-background-100 px-4 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-50 text-primary-500">
                <i className="ri-search-eye-line text-2xl leading-none" />
              </span>
              <h2 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                {keyword ? "未找到相关内容，建议更换关键词" : "还没有输入关键词"}
              </h2>
              <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-600">
                {keyword
                  ? "可以尝试更短的关键词，或使用影视类型（如「悬疑」「古装」）、地区、导演与演员姓名进行搜索。"
                  : "在上方搜索框输入影视名称或资讯标题，即可找到对应的剧情介绍与资讯内容。"}
              </p>
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                {SUGGESTIONS.slice(0, 4).map((s) => (
                  <Link
                    key={s}
                    to={`/search?q=${encodeURIComponent(s)}`}
                    className="cursor-pointer whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    {s}
                  </Link>
                ))}
              </div>
              <div className="mt-5 flex flex-wrap justify-center gap-3">
                <Link
                  to="/category"
                  className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  浏览影视分类
                </Link>
                <Link
                  to="/"
                  className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300"
                >
                  返回首页
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}